// How a cash verdict is rendered, kept in a plain .ts module with no JSX so
// Node's test runner can check it under --experimental-strip-types.
//
// THE RULE THIS FILE EXISTS TO ENFORCE. Six of the nine cash statuses are facts
// about FLEETLY'S RECORDS and only three are questions about a driver. If the
// screen paints them all in the same red, a broken webhook and an unbanked
// thousand rand look identical, and the first person to be asked about it is a
// man who did nothing. So tone is derived here, once, and every component reads
// it from here.
//
//   red     something to ask a person about
//   amber   we hold part of the picture; fix our side before asking anything
//   neutral we hold nothing; this says nothing either way
//   green   accounted for

export type ChipTone = 'green' | 'red' | 'amber' | 'neutral' | 'brand'

export type CashStatus =
  | 'BALANCED'
  | 'SHORTFALL'
  | 'SURPLUS'
  | 'MISSING_UBER_CASH'
  | 'MISSING_DECLARATION'
  | 'MISSING_DECLARATION_PART'
  | 'NO_DECLARATION_ERA'
  | 'NOT_APPLICABLE'

export type VarianceBound = 'exact' | 'indicative' | 'unknown'

const CASH_STATUS: Record<CashStatus, { label: string; tone: ChipTone; isFinding: boolean }> = {
  BALANCED: { label: 'Balanced', tone: 'green', isFinding: false },
  SHORTFALL: { label: 'Not accounted for', tone: 'red', isFinding: true },
  SURPLUS: { label: 'More banked than collected', tone: 'amber', isFinding: true },
  MISSING_DECLARATION: { label: 'No submission on file', tone: 'amber', isFinding: true },
  MISSING_DECLARATION_PART: { label: 'Part of the form missing', tone: 'amber', isFinding: false },
  MISSING_UBER_CASH: { label: 'Uber cash not loaded', tone: 'neutral', isFinding: false },
  NO_DECLARATION_ERA: { label: 'Before we captured declarations', tone: 'neutral', isFinding: false },
  NOT_APPLICABLE: { label: 'Cancelled shift', tone: 'neutral', isFinding: false },
}

export function cashStatusLabel(status: string): string {
  return CASH_STATUS[status as CashStatus]?.label ?? status
}

export function cashStatusTone(status: string): ChipTone {
  return CASH_STATUS[status as CashStatus]?.tone ?? 'neutral'
}

/** True only for the statuses a person should act on. Everything else is our gap. */
export function cashStatusIsFinding(status: string): boolean {
  return CASH_STATUS[status as CashStatus]?.isFinding ?? false
}

export type BankStatus = 'CLEARED' | 'PART_CLEARED' | 'NOT_FOUND_ON_STATEMENT' | 'NO_STATEMENT_LOADED'

const BANK_STATUS: Record<BankStatus, { label: string; tone: ChipTone }> = {
  CLEARED: { label: 'Reached the Fleetly account', tone: 'green' },
  PART_CLEARED: { label: 'Partly matched', tone: 'amber' },
  NOT_FOUND_ON_STATEMENT: { label: 'Not on the statement', tone: 'red' },
  // Neutral, never red. Nobody uploaded the statement; that is not the driver's doing.
  NO_STATEMENT_LOADED: { label: 'No statement loaded', tone: 'neutral' },
}

export function bankStatusLabel(status: string): string {
  return BANK_STATUS[status as BankStatus]?.label ?? status
}
export function bankStatusTone(status: string): ChipTone {
  return BANK_STATUS[status as BankStatus]?.tone ?? 'neutral'
}

export type FuelStatus =
  | 'CASH_CLAIM_MATCHES'
  | 'CLAIM_EXCEEDS_RECEIPTS'
  | 'RECEIPTS_EXCEED_CLAIM'
  | 'CLAIM_WITH_NO_TRANSACTION'
  | 'NO_FUEL_THIS_SHIFT'
  | 'NO_FUEL_DATA_LOADED'

const FUEL_STATUS: Record<FuelStatus, { label: string; tone: ChipTone }> = {
  CASH_CLAIM_MATCHES: { label: 'Claim matches receipts', tone: 'green' },
  CLAIM_EXCEEDS_RECEIPTS: { label: 'Claimed more than the reports show', tone: 'red' },
  RECEIPTS_EXCEED_CLAIM: { label: 'Reports show more than claimed', tone: 'amber' },
  CLAIM_WITH_NO_TRANSACTION: { label: 'Claim with no transaction found', tone: 'amber' },
  NO_FUEL_THIS_SHIFT: { label: 'No petrol bought', tone: 'neutral' },
  NO_FUEL_DATA_LOADED: { label: 'No fuel report loaded', tone: 'neutral' },
}

export function fuelStatusLabel(status: string): string {
  return FUEL_STATUS[status as FuelStatus]?.label ?? status
}
export function fuelStatusTone(status: string): ChipTone {
  return FUEL_STATUS[status as FuelStatus]?.tone ?? 'neutral'
}

export type PresenceStatus =
  | 'AT_STATION'
  | 'MOVING_ELSEWHERE'
  | 'STOPPED_ELSEWHERE'
  | 'MOVING_LOCATION_UNKNOWN'
  | 'STOPPED_LOCATION_UNKNOWN'
  | 'NO_TELEMETRY'
  | 'VEHICLE_UNRESOLVED'

const PRESENCE: Record<PresenceStatus, { label: string; tone: ChipTone }> = {
  AT_STATION: { label: 'Car was at the station', tone: 'green' },
  MOVING_ELSEWHERE: { label: 'Car was driving elsewhere', tone: 'red' },
  STOPPED_ELSEWHERE: { label: 'Car was parked elsewhere', tone: 'amber' },
  MOVING_LOCATION_UNKNOWN: { label: 'Car moving, station not mapped', tone: 'amber' },
  STOPPED_LOCATION_UNKNOWN: { label: 'Car stopped, station not mapped', tone: 'neutral' },
  NO_TELEMETRY: { label: 'No Cartrack data', tone: 'neutral' },
  VEHICLE_UNRESOLVED: { label: 'Vehicle not matched', tone: 'neutral' },
}

export function presenceLabel(status: string): string {
  return PRESENCE[status as PresenceStatus]?.label ?? status
}
export function presenceTone(status: string): ChipTone {
  return PRESENCE[status as PresenceStatus]?.tone ?? 'neutral'
}

/**
 * Rand, or an em-space when the figure is genuinely absent.
 *
 * CLAUDE.md rule 6: a missing figure shows as missing, never as zero. R0.00
 * against a driver's name reads as "he banked nothing", and on this screen most
 * blanks mean "we have not loaded the file yet".
 */
export function zar(value: number | string | null | undefined, opts?: { signed?: boolean }): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  const body = Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const sign = n < 0 ? '-' : opts?.signed && n > 0 ? '+' : ''
  return `${sign}R${body}`
}

export function num(value: number | string | null | undefined, digits = 0): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-ZA', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

/** 'YYYY-MM-01' for the month N months back from today, for the month picker. */
/**
 * Every month from `from` to today, newest first.
 *
 * WHY NOT A ROLLING COUNT. monthOptions below counts back a fixed number of
 * months from today, so on 19 September 2026 a list of 14 reached back only to
 * August 2025 while Fleetly's first shift was 3 February 2025. Seven months of
 * the company's own trading were unreachable from the picker, and the list got
 * one month shorter at the far end every time a month turned. Nobody notices
 * that happening; they notice it two years later when a month they need is gone.
 *
 * So the list is anchored at the beginning and grows. `from` comes from the
 * database, the first shift Fleetly ever recorded, which means this never needs
 * a number changed in it again.
 */
export function monthOptionsFrom(
  from: string | null | undefined,
  today = new Date(),
): { value: string; label: string }[] {
  const start = from ? new Date(`${String(from).slice(0, 7)}-01T00:00:00Z`) : null
  if (!start || Number.isNaN(start.getTime())) return monthOptions(14, today)

  const out: { value: string; label: string }[] = []
  const cursor = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))
  // Guard rather than trust: a bad date in the data must not spin the browser.
  for (let i = 0; i < 600 && cursor >= start; i++) {
    out.push({
      value: `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}-01`,
      label: cursor.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    })
    cursor.setUTCMonth(cursor.getUTCMonth() - 1)
  }
  return out
}

export function monthOptions(count = 13, today = new Date()): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = []
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i, 1))
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`
    const label = d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    out.push({ value, label })
  }
  return out
}

/**
 * True for a month in which the cockpit holds no driver declaration at all.
 *
 * IT DOES NOT MEAN NOBODY WAS ASKED, AND SAYING SO WAS WRONG.
 *
 * The daily Typeform qind02rW has carried "Please type in your cash deposits for
 * the shift" since 3 February 2025, and every response answers it. Typeform holds
 * 12,466 of them between then and 23 July 2026, plus 10,070 Part B responses over
 * the same period. None has ever been imported: shift_form_submissions begins on
 * 23 July 2026, the day the webhook was wired, and the legacy Google Sheets import
 * brought in fuel and carwash and left the deposit answers behind.
 *
 * So the driver answered, and the cockpit cannot see it. The cell says "not
 * imported", which is the truth and is also the work.
 *
 * WHY IT IS A FUNCTION AND NOT A DATE. The obvious version compares the month
 * against 23 July 2026, which hard-codes a fact that lives in system_settings and
 * that the import will move. This asks the data: a month where declaring_shifts is
 * zero has no declaration in this database, whatever the calendar says.
 */
export function noDeclarationOnFile(row: { declaring_shifts?: unknown } | null | undefined): boolean {
  if (!row) return false
  return Number(row.declaring_shifts ?? 0) === 0
}
